import { FocusEvent, ChangeEvent, useCallback, useEffect, useRef, useMemo, FormEvent } from 'react';
import { isEqual } from 'lodash';

import { useStoreActions, useStoreState } from '~/store';

import { Field, FieldValue, FormState, FieldErrors } from './store';

export type FormValues<T> = {
  [key in keyof T]: FieldValue
}
export type FormErrors<T> = {
  [key in keyof T]: FieldErrors
}

type HTMLFieldElements = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement

type FormSubmitPayload<T> = {
  formState: FormState
  values: FormValues<T>
  errors: FormErrors<T>
}

export type UseFormOptionFields = ([string, Partial<Field>] | string)[];

export interface UseFormOptions<T> {
  formName: string
  fields?: UseFormOptionFields
  validationTrigger?: 'onSubmit' | 'onBlur' | 'onChange'
  defaultValues?: FormValues<T>
  onChange?: (values: FormValues<T>) => void
}

export interface UseFormData<T> {
  formState: FormState
  removeForm: () => void
  values: FormValues<T>
  defaultValues?: FormValues<T>
  setValue: (name: keyof T, value: any) => void
  triggerValidation: (name: keyof T, value: any) => FieldErrors
  errors: FormErrors<T>
  fieldEvents: {
    onBlur: (data: FocusEvent<HTMLFieldElements>) => void
    onChange: (data: ChangeEvent<HTMLFieldElements>) => void
  },
  formEvents: {
    onSubmit: (callback: (data: FormSubmitPayload<T>) => void) => (e?: FormEvent<HTMLFormElement>) => void
  }
}

export interface UseForm {
  <T>(options: UseFormOptions<T>): UseFormData<T>
}

const useForm: UseForm = <T>(options: UseFormOptions<T>) => {
  const fields = useRef<UseFormOptionFields | undefined>(options.fields);
  const defaultValues = useRef<FormValues<T> | undefined>(options.defaultValues);

  const storeFormFields = useStoreState(state => state.form.formFields(options.formName));
  const storeFormState = useStoreState(state => state.form.formState(options.formName));
  const storeAddForm = useStoreActions(actions => actions.form.addForm);
  const storeUpdateForm = useStoreActions(actions => actions.form.updateForm);
  const storeRemoveForm = useStoreActions(actions => actions.form.removeForm);

  const storeValues = useStoreState(state => state.form.getValues(options.formName));
  const storeSetValue = useStoreActions(actions => actions.form.setValue);

  const storeValidators = useStoreState(state => state.form.getValidators(options.formName));

  const storeErrors = useStoreState(state => state.form.getErrors(options.formName));
  const storeAddError = useStoreActions(actions => actions.form.addError);
  const storeRemoveError = useStoreActions(actions => actions.form.removeError);

  const storeSetDefaultValues = useStoreActions(actions => actions.form.setDefaultValues);

  const storeFormFieldsRef = useRef(storeFormFields);
  storeFormFieldsRef.current = storeFormFields;
  const storeFormStateRef = useRef(storeFormState);
  storeFormStateRef.current = storeFormState;
  const storeValuesRef = useRef(storeValues);
  storeValuesRef.current = storeValues;
  const storeErrorsRef = useRef(storeErrors);
  storeErrorsRef.current = storeErrors;
  const storeValidatorsRef = useRef(storeValidators);
  storeValidatorsRef.current = storeValidators;

  const removeForm = useCallback(() => {
    storeRemoveForm(options.formName)
  }, [options.formName, storeRemoveForm]);

  const addError = useCallback((name, error) => {
    storeAddError({ formName: options.formName, name, error });
  }, [options.formName, storeAddError]);
  const removeError = useCallback((name, error) => {
    storeRemoveError({ formName: options.formName, name, error });
  }, [options.formName, storeRemoveError]);

  const triggerValidation = useCallback((name: keyof T, value: any): FieldErrors => {
    const errors: FieldErrors = [];
    const validators = storeValidatorsRef.current;
    const formFields = storeFormFieldsRef.current;

    if (validators[(name as string)]) {
      Object.keys(validators[(name as string)]).forEach(key => {
        const field = formFields[name as string];

        const validationResult = validators[(name as string)][key](
          { name, value, errors: field.errors, state: field.state },
          { state: storeFormStateRef.current, values: (storeValuesRef.current as FormValues<T>), errors: (storeErrorsRef.current as FormErrors<T>)  }
        );

        if (validationResult) {
          removeError(name, key);
        } else {
          addError(name, key);

          errors.push(key);
        }
      });
    }

    return errors;
  }, [addError, removeError]);

  const setValue = useCallback((name, value) => {
    const errorsKeys = Object.keys(storeErrorsRef.current);
    const formFields = storeFormFieldsRef.current;

    if (options.validationTrigger === 'onChange' || errorsKeys.length) {
      errorsKeys.forEach(key => {
        triggerValidation(key as keyof T, (key === name ? value : formFields[key].value));
      });
    }

    storeSetValue({ formName: options.formName, name, value });
  }, [options.formName, options.validationTrigger, storeSetValue, triggerValidation]);

  const getInputValue = useCallback(
    (type, value) => (type === 'number' ? (value === '' ? null : +value) : value),
    []
  );

  const onBlur = useCallback(({ target: { type, name, value } }) => {
    value = getInputValue(type, value);

    if (!options.validationTrigger || options.validationTrigger === 'onBlur') {
      triggerValidation(name, value);
    }
  }, [getInputValue, options.validationTrigger, triggerValidation]);
  const onChange = useCallback(({ target: { type, name, value } }) => {
    value = getInputValue(type, value);

    setValue(name, value);
  }, [getInputValue, setValue]);

  const onSubmit = useCallback(callback => e => {
    if (e) {
      e.preventDefault();
    }

    const errors = {};
    
    Object.keys(storeValidators).forEach(name => {
      const e = triggerValidation((name as keyof T), (storeValues || {})[name]);
      if (e.length) {
        errors[name] = e;
      }
    });
    
    callback({
      formState: { ...storeFormState, invalid: !!Object.keys(errors).length },
      values: storeValues,
      errors
    });
  }, [storeFormState, storeValidators, storeValues, triggerValidation]);
  
  const fieldEvents = useMemo(() => ({ onBlur, onChange }), [onBlur, onChange]);
  const formEvents = useMemo(() => ({ onSubmit }), [onSubmit]);

  useEffect(() => {
    if (fields.current) {
      const fieldsData = {};

      fields.current.forEach(field => {
        if (typeof field === 'string') {
          fieldsData[field] = {};
        } else if (Array.isArray(field)) {
          fieldsData[field[0]] = (field[1] || {});
        }
      });

      storeAddForm({ formName: options.formName, fields: fieldsData });
    }
  }, [options.formName, storeAddForm]);

  useEffect(() => {
    if (options.fields && fields.current && !isEqual(options.fields, fields.current)) {
      const fieldsData = {};

      options.fields.forEach(field => {
        if (typeof field === 'string') {
          fieldsData[field] = {};
        } else if (Array.isArray(field)) {
          fieldsData[field[0]] = (field[1] || {});
        }
      });

      storeUpdateForm({ formName: options.formName, fields: fieldsData });
    }
  }, [options.fields, options.formName, storeAddForm, storeUpdateForm]);

  useEffect(() => {
    if (defaultValues.current) {
      storeSetDefaultValues({ formName: options.formName, defaultValues: defaultValues.current });
    }
  }, [options.formName, storeSetDefaultValues]);

  useEffect(() => {
    if (options.onChange) {
      options.onChange((storeValues as FormValues<T>));
    }
  }, [options, storeValues]);

  return {
    formState: storeFormState,
    removeForm,
    values: (storeValues as FormValues<T>),
    defaultValues: defaultValues.current,
    setValue,
    triggerValidation,
    errors: (storeErrors as FormErrors<T>),
    fieldEvents,
    formEvents
  }
};

export default useForm
